// packages/core/src/diff/views.ts
import type { ViewDeclV2 } from "../v010-ast.js";
import type { ViewDiff, Status } from "../diff-types.js";

/**
 * Canonical fingerprint of a view's body for change detection.
 * Strips all SourceSpan fields (source/nameSource/idSource/…) before
 * serialising so position-only edits don't generate false positives.
 */
function stripSource(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stripSource);
  if (v !== null && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (
        k === "source" ||
        k === "nameSource" ||
        k === "idSource" ||
        k === "parentSource" ||
        k === "extentSource"
      )
        continue;
      out[k] = stripSource(val);
    }
    return out;
  }
  return v;
}

function fingerprint(v: ViewDeclV2): string {
  return JSON.stringify(
    stripSource({
      description: v.description,
      header: v.header,
      clauses: v.clauses,
      instanceOf: v.instanceOf,
      bindings: v.bindings,
    })
  );
}

/**
 * Diff two view lists by name.
 *
 * Matched views are compared by a canonical body fingerprint (source spans
 * stripped). A changed fingerprint → "modified". Added/removed detected by
 * name presence. Field-level per-clause diff is deferred (fields map stays
 * empty; status conveys the verdict).
 */
export function diffViews(
  before: readonly ViewDeclV2[],
  after: readonly ViewDeclV2[]
): Map<string, ViewDiff> {
  const byB = new Map(before.map((v) => [v.name, v]));
  const byA = new Map(after.map((v) => [v.name, v]));
  const out = new Map<string, ViewDiff>();
  const all = new Set([...byB.keys(), ...byA.keys()]);
  for (const name of all) {
    const b = byB.get(name);
    const a = byA.get(name);
    if (b && a) {
      const status: Status = fingerprint(b) === fingerprint(a) ? "unchanged" : "modified";
      out.set(name, { id: name, status, fields: new Map(), sourceFile: a.source?.file });
    } else if (a) {
      out.set(name, { id: name, status: "added", fields: new Map(), sourceFile: a.source?.file });
    } else if (b) {
      out.set(name, { id: name, status: "removed", fields: new Map(), sourceFile: b.source?.file });
    }
  }
  return out;
}
